import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch, ApiError } from '../lib/api'
import { getToken, decodeToken } from '../lib/auth'
import { verbRule } from '../lib/roleVerbs'
import type { TaskQueueItem } from '../lib/tasks'
import {
  obligationLabel,
  obligationExplanation,
  statusLabel,
  acceptedSourcesLabel,
  failureLabel,
} from '../lib/claims'
import { cleanEngineText, formatOfficerDate } from '../lib/officerFormat'
import { HelpDot } from './guidance/Guidance'
import Sentence from './Sentence'

/* Chase pane — IO ledger screen `tasks_chase`.
 * The outside world owes us a document. Two task shapes:
 *   investigation_action → create a request, preview the draft email
 *     (r-composer-preview), send (c-button). Waive instead is available
 *     (justification required) — officer-level on obligation rows
 *     (Ale 2026-07-08: waivers officer-only at pilot, 4-eyes parked
 *     post-pilot), manager-gated on legacy gaps. The task's target is
 *     POLYMORPHIC (P6b cutover):
 *       target_type = 'evidence_status'    → a Policy v2 obligation row;
 *         read / request / waive go through /api/evidence-status/* and
 *         the pane speaks CLAIMS language (obligation identity, accepted
 *         sources, precise gate failures).
 *       target_type = 'investigation_gap'  → the legacy gap; the
 *         /api/gaps/* path stays for the transition window (flag-OFF
 *         rollback + any straggler tasks).
 *   request_overdue (target = document request) → SLA timeline
 *     (r-sla-timeline) + draft email on demand + escalate (c-button) —
 *     the Escalate card renders ONLY when the fund names a senior contact
 *     (audit cut 11, Ale 2026-09-05); the record carries none today.
 * Requests auto-fulfil when the owed document arrives (lst_request_fulfill)
 * — this pane never closes anything by hand. */

interface Gap {
  id: string
  fund_id: string
  requirement_id: string
  target_type: string
  target_id: string
  status: string
  waiver_justification: string | null
}

/** Policy v2 obligation row + its obligation identity —
 *  GET /api/evidence-status/{id} (EvidenceStatusDetailResponse). */
interface ObligationRow {
  id: string
  object_type: string
  object_id: string
  obligation_code: string
  status: string
  waived_reason: string | null
  obligation: {
    code: string
    family: string
    level: string
    is_mandatory: boolean
    accepted_sources: string[]
    description: string
  } | null
  failures: Array<{
    failure: string
    document_id: string | null
    detail: string | null
    recorded_at: string
  }>
}

interface DocRequest {
  id: string
  investigation_gap_id: string | null
  evidence_status_id: string | null
  fund_id: string
  counterparty_name: string
  status: string
  priority: string | null
  deadline: string | null
  sent_at: string | null
  fulfilled_at: string | null
  recipient_email: string | null
  created_at: string
}

interface DraftEmail {
  recipient_email: string | null
  subject: string
  body: string
  warning: string | null
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

const STEP_DONE = { color: 'var(--verified)' }
const STEP_TODO = { color: 'var(--faint)' }

function SlaTimeline({ req }: { req: DocRequest }) {
  const overdue =
    req.status === 'overdue' ||
    req.status === 'escalated' ||
    (req.deadline && !req.fulfilled_at && new Date(req.deadline) < new Date())
  const steps: Array<{ label: string; date: string | null; done: boolean; bad?: boolean }> = [
    { label: 'drafted', date: formatOfficerDate(req.created_at), done: true },
    { label: 'sent', date: formatOfficerDate(req.sent_at), done: !!req.sent_at },
    {
      label: 'deadline',
      date: formatOfficerDate(req.deadline),
      done: !!req.deadline,
      bad: !!overdue && !req.fulfilled_at,
    },
    {
      label: req.status === 'cancelled' ? 'cancelled' : 'fulfilled',
      date: formatOfficerDate(req.fulfilled_at),
      done: !!req.fulfilled_at || req.status === 'cancelled',
    },
  ]
  return (
    <div style={{ display: 'flex', gap: 0, marginTop: 10 }}>
      {steps.map((s, i) => (
        <div key={s.label} style={{ flex: 1, position: 'relative' }}>
          {i > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 5,
                left: '-50%',
                width: '100%',
                height: 2,
                background: s.done ? 'var(--verified)' : 'var(--line)',
              }}
            />
          )}
          <div
            style={{
              position: 'relative',
              width: 12,
              height: 12,
              borderRadius: 99,
              margin: '0 auto',
              background: s.bad ? 'var(--gap)' : s.done ? 'var(--verified)' : '#fff',
              border: `2px solid ${s.bad ? 'var(--gap)' : s.done ? 'var(--verified)' : 'var(--line)'}`,
            }}
          />
          <div
            style={{
              textAlign: 'center',
              fontSize: 'var(--text-12)',
              marginTop: 5,
              ...(s.done || s.bad ? STEP_DONE : STEP_TODO),
              ...(s.bad ? { color: 'var(--gap)', fontWeight: 700 } : {}),
            }}
          >
            {s.label}
          </div>
          {s.date && (
            <div
              className="mono"
              style={{ textAlign: 'center', fontSize: 'var(--text-12)', color: 'var(--faint)' }}
            >
              {s.date}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function DraftPreview({ requestId }: { requestId: string }) {
  const [draft, setDraft] = useState<DraftEmail | null>(null)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open || draft) return
    apiFetch<DraftEmail>(`/api/requests/${requestId}/draft-email`)
      .then(setDraft)
      .catch(() =>
        setDraft({ recipient_email: null, subject: '—', body: 'draft unavailable', warning: null })
      )
  }, [open, draft, requestId])
  if (!open)
    return (
      <button className="vbtn" style={{ marginTop: 10 }} onClick={() => setOpen(true)}>
        View draft email
      </button>
    )
  return (
    <div
      style={{
        marginTop: 10,
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: '10px 13px',
        background: '#fafbfc',
      }}
    >
      {!draft ? (
        <span style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>loading…</span>
      ) : (
        <>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
            to <span className="mono">{draft.recipient_email ?? 'no contact on file'}</span>
          </div>
          <div style={{ fontSize: 'var(--text-12)', fontWeight: 600, marginTop: 4 }}>
            {draft.subject}
          </div>
          <pre
            style={{
              font: 'inherit',
              fontSize: 'var(--text-12)',
              whiteSpace: 'pre-wrap',
              color: 'var(--muted)',
              marginTop: 6,
              marginBottom: 0,
            }}
          >
            {draft.body}
          </pre>
          {draft.warning && (
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--expired)', marginTop: 6 }}>
              ⚠ {draft.warning}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* The fund's contact fields as the pane reads them (GET /api/funds/{id}). */
interface FundContacts {
  client_contact_name: string | null
  client_contact_email: string | null
  /* Senior / escalation contact — NOT on the record today (FundResponse
   * carries client_contact_* only). Named here so the Escalate card's
   * condition has something to read the day the counterparty register
   * lands; until then it is always absent. */
  senior_contact_name?: string | null
  senior_contact_email?: string | null
}

interface SeniorContact {
  name: string
  email: string | null
}

/* Audit cut 11 (Ale 2026-09-05): "Escalate" is a verb with an addressee —
 * without a senior contact on the fund there is nobody to escalate to, so
 * the card does not render. Keep this the ONE place that decides. */
function seniorContactFor(fund: FundContacts | null): SeniorContact | null {
  const name = fund?.senior_contact_name?.trim()
  if (!name) return null
  return { name, email: fund?.senior_contact_email ?? null }
}

/* Client chase contact (Ale ruling 2026-08-30): chase letters are addressed
 * to the FUND's responsible client contact — fed at onboarding (the
 * depositary-agreement signing step) and maintainable right here, where the
 * chase actually leaves. Without a stored contact the letters keep the
 * legacy fallback (counterparty / "Unknown"), so this card nudges the
 * officer to fill it in. */
function ChaseContactCard({
  fundId,
  onFund,
}: {
  fundId: string
  /** The fund as read — the pane derives the senior contact from it. */
  onFund?: (fund: FundContacts) => void
}) {
  const [name, setName] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftEmail, setDraftEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    apiFetch<FundContacts>(`/api/funds/${fundId}`)
      .then((f) => {
        if (!alive) return
        setName(f.client_contact_name)
        setEmail(f.client_contact_email)
        setLoaded(true)
        onFund?.(f)
      })
      .catch(() => {
        if (alive) setLoaded(true)
      })
    return () => {
      alive = false
    }
  }, [fundId, onFund])

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const f = await apiFetch<{
        client_contact_name: string | null
        client_contact_email: string | null
      }>(`/api/funds/${fundId}/contact`, {
        method: 'PATCH',
        body: JSON.stringify({
          client_contact_name: draftName.trim() || null,
          client_contact_email: draftEmail.trim() || null,
        }),
      })
      setName(f.client_contact_name)
      setEmail(f.client_contact_email)
      setEditing(false)
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  if (!loaded) return null
  const inputStyle = {
    width: '100%',
    font: 'inherit',
    fontSize: 'var(--text-12)',
    padding: '7px 10px',
    border: '1.5px solid var(--line)',
    borderRadius: 7,
  } as const
  return (
    <div className="vcard" style={{ padding: '12px 16px' }} data-testid="chase-contact-card">
      <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
        {name || email ? (
          <>
            chases go to:{' '}
            <span style={{ color: 'var(--muted)' }}>
              {name ?? ''}
              {email ? (name ? ` <${email}>` : email) : ''}
            </span>
          </>
        ) : (
          'no client contact stored — letters fall back to the counterparty'
        )}{' '}
        {!editing && (
          <button
            className="vbtn"
            style={{ fontSize: 'var(--text-12)', padding: '2px 8px', marginLeft: 6 }}
            onClick={() => {
              setDraftName(name ?? '')
              setDraftEmail(email ?? '')
              setEditing(true)
            }}
          >
            {name || email ? 'edit' : 'add contact'}
          </button>
        )}
      </div>
      {editing && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
            Who do chase letters go to? A distribution address is fine.
          </div>
          <input
            placeholder="Responsible contact name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            style={inputStyle}
          />
          <input
            placeholder="Contact email"
            value={draftEmail}
            onChange={(e) => setDraftEmail(e.target.value)}
            style={inputStyle}
          />
          {error && <div style={{ fontSize: 'var(--text-12)', color: 'var(--gap)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="vbtn pri" onClick={save} disabled={busy}>
              Save contact
            </button>
            <button className="vbtn" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function ChasePane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const role = decodeToken(getToken() ?? '')?.role ?? 'ops_officer'
  const isGapTask = task.task_type === 'investigation_action'
  // Role honesty (Tier-1 C1): the waive door is rendered per the rank the
  // backend enforces TODAY — obligation rows = any ops officer (Ale
  // 2026-07-08), legacy gaps = manager. A role below it sees the door
  // disabled with the rule, never a hidden door and never a live 403.
  const waiveRule = verbRule(
    task.target_type === 'evidence_status' ? 'waive_obligation' : 'waive_gap',
    role
  )
  // P6b polymorphic routing: an investigation_action task either targets a
  // Policy v2 obligation row (evidence_status) or a legacy gap.
  const isObligationTask = isGapTask && task.target_type === 'evidence_status'

  const [gap, setGap] = useState<Gap | null>(null)
  const [obRow, setObRow] = useState<ObligationRow | null>(null)
  const [req, setReq] = useState<DocRequest | null>(null)
  const [targetTitle, setTargetTitle] = useState<string | null>(null)
  const [waiveOpen, setWaiveOpen] = useState(false)
  const [justification, setJustification] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  // Is the first read still in flight? Drives the Create-request button's
  // LABEL only — never its enabled state (see requestPath below).
  const [loading, setLoading] = useState(true)
  // The fund's senior contact, read off the same GET the contact card makes
  // (audit cut 11): null today, since the record has no such field.
  const [senior, setSenior] = useState<SeniorContact | null>(null)
  const onFund = useCallback((f: FundContacts) => setSenior(seniorContactFor(f)), [])

  const load = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      if (isObligationTask && task.target_id) {
        const row = await apiFetch<ObligationRow>(`/api/evidence-status/${task.target_id}`)
        setObRow(row)
        // Fund-scoped reads only when the task HAS a fund: since the
        // fund-less-task fix (#982) these cards are actionable with a
        // null fund, and `/api/funds/null/requests` 422s — the raw
        // status then rendered as the card's headline.
        if (task.fund_id) {
          const reqs = await apiFetch<DocRequest[]>(`/api/funds/${task.fund_id}/requests`)
          setReq(
            reqs.find((r) => r.evidence_status_id === row.id && r.status !== 'cancelled') ?? null
          )
          // 'asset' obligation rows live in entities.
          const objectType = row.object_type === 'asset' ? 'entity' : row.object_type
          if (['entity', 'instrument', 'position', 'trade'].includes(objectType)) {
            apiFetch<{ title: string }>(
              `/api/funds/${task.fund_id}/objects/${objectType}/${row.object_id}`
            )
              .then((d) => setTargetTitle(d.title))
              .catch(() => {})
          }
        }
      } else if (isGapTask && task.target_id) {
        const g = await apiFetch<Gap>(`/api/gaps/${task.target_id}`)
        setGap(g)
        if (!task.fund_id) return
        const reqs = await apiFetch<DocRequest[]>(`/api/funds/${task.fund_id}/requests`)
        setReq(
          reqs.find((r) => r.investigation_gap_id === g.id && r.status !== 'cancelled') ?? null
        )
        if (['entity', 'instrument', 'position', 'trade'].includes(g.target_type)) {
          apiFetch<{ title: string }>(
            `/api/funds/${task.fund_id}/objects/${g.target_type}/${g.target_id}`
          )
            .then((d) => setTargetTitle(d.title))
            .catch(() => {})
        }
      } else if (task.target_id && task.fund_id) {
        const reqs = await apiFetch<DocRequest[]>(`/api/funds/${task.fund_id}/requests`)
        setReq(reqs.find((r) => r.id === task.target_id) ?? null)
      }
    } catch (e) {
      setError(errText(e))
    } finally {
      setLoading(false)
    }
  }, [isGapTask, isObligationTask, task.fund_id, task.target_id])

  useEffect(() => {
    load()
  }, [load])

  /* THE single condition that decides whether a request can be created —
   * and, when it can, where it goes. `null` means "we cannot chase yet".
   *
   * The Create-request button's disabled state and createRequest()'s own
   * guard BOTH read this value. That is deliberate and load-bearing: the two
   * used to be separate (`disabled={busy}` on one side, `if (!gap && !obRow)
   * return` on the other), so the button rendered ENABLED during the window
   * before load() resolved and a click in that window did nothing at all —
   * no request, no error, no busy state. The officer pressed a live-looking
   * control and reasonably concluded the system had acted.
   *
   * Ale ruling 2026-08-13: no control may silently do nothing. Disabled-
   * until-known is honest about the one thing we actually know, which is
   * that we don't know yet. Queuing the intent was rejected — it makes the
   * button lie in the other direction, appearing to have acted before we
   * know the action is even valid.
   *
   * Do NOT reintroduce a second condition here that "happens to agree".
   */
  const requestPath = useMemo(() => {
    // Obligation rows chase through the evidence-status endpoint; the
    // backend derives the counterparty exactly like the legacy path.
    if (obRow) return `/api/evidence-status/${obRow.id}/request`
    if (gap) return `/api/funds/${task.fund_id}/gaps/${gap.id}/request`
    return null
  }, [obRow, gap, task.fund_id])

  async function createRequest() {
    if (!requestPath) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiFetch<{ request: DocRequest; warning: string | null }>(requestPath, {
        method: 'POST',
      })
      setReq(r.request)
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  async function transition(status: 'sent' | 'escalated') {
    if (!req) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiFetch<DocRequest>(`/api/requests/${req.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
      setReq(r)
      if (status === 'sent')
        setDone(
          'Request sent. It fulfils itself when the document arrives — nothing else to do here.'
        )
      if (status === 'escalated')
        setDone(
          "Escalated. The counterparty's senior contact is now on the hook; the SLA timeline keeps tracking."
        )
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  async function waive() {
    if (!gap && !obRow) return
    setBusy(true)
    setError(null)
    try {
      await apiFetch(
        obRow ? `/api/evidence-status/${obRow.id}/waive` : `/api/gaps/${gap!.id}/waive`,
        {
          method: 'PATCH',
          body: JSON.stringify({ justification }),
        }
      )
      setDone(
        obRow
          ? 'Obligation waived — recorded with your justification. It counts as covered; the chase task closes with it.'
          : 'Gap waived — recorded with your justification. The investigation task closes with it.'
      )
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  if (done)
    return (
      <div className="vcard" style={{ flex: 1, alignSelf: 'flex-start', padding: 22 }}>
        <div style={{ fontSize: 'var(--text-13)', color: 'var(--verified)', fontWeight: 600 }}>
          ✓ {done}
        </div>
        <button className="vbtn pri" style={{ marginTop: 16 }} onClick={onResolved}>
          Next task →
        </button>
      </div>
    )

  return (
    <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* context: what is owed and why */}
      <div className="vcard" style={{ flex: 1, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
            {isObligationTask
              ? `Evidence needed — ${obligationLabel(obRow?.obligation_code ?? (task.task_data as { obligation_code?: string } | null)?.obligation_code)}${
                  targetTitle
                    ? ` on ${targetTitle}`
                    : obRow
                      ? ` on ${obRow.object_type.replace(/_/g, ' ')}`
                      : ''
                }`
              : isGapTask
                ? `Evidence gap${targetTitle ? ` on ${targetTitle}` : gap ? ` on ${gap.target_type.replace(/_/g, ' ')}` : ''}`
                : `Document request overdue${req ? ` — ${req.counterparty_name}` : ''}`}{' '}
            <HelpDot k="pane.chase" />
          </div>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 2 }}>
            {isObligationTask
              ? (obligationExplanation(obRow?.obligation_code) ??
                'the policy requires a document that proves this claim — it must come from outside')
              : isGapTask
                ? 'the record item lacks a document the policy requires — it must come from outside'
                : 'the counterparty missed the deadline — keep the pressure on'}
          </div>
        </div>
        <div style={{ padding: '14px 18px' }}>
          {error && (
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', marginBottom: 10 }}>
              {error}
            </div>
          )}
          {gap && (
            <div
              style={{
                fontSize: 'var(--text-12)',
                display: 'flex',
                gap: '4px 18px',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ color: 'var(--muted)' }}>
                gap status{' '}
                <span className={`chip s-${gap.status === 'open' ? 'gap' : 'uncovered'}`}>
                  {statusLabel(gap.status)}
                </span>
              </span>
              <span style={{ color: 'var(--muted)' }}>
                target{' '}
                <span className="mono" style={{ color: 'var(--ink)', fontWeight: 600 }}>
                  {targetTitle ?? gap.target_type}
                </span>
              </span>
            </div>
          )}
          {obRow && (
            <>
              <div
                style={{
                  fontSize: 'var(--text-12)',
                  display: 'flex',
                  gap: '4px 18px',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ color: 'var(--muted)' }}>
                  status{' '}
                  <span className={`chip s-${obRow.status === 'open' ? 'gap' : 'uncovered'}`}>
                    {statusLabel(obRow.status)}
                  </span>
                </span>
                <span style={{ color: 'var(--muted)' }}>
                  target{' '}
                  <span className="mono" style={{ color: 'var(--ink)', fontWeight: 600 }}>
                    {targetTitle ?? obRow.object_type}
                  </span>
                </span>
                {obRow.obligation && (
                  <span style={{ color: 'var(--muted)' }}>
                    <span
                      className={`chip s-${obRow.obligation.is_mandatory ? 'gap' : 'uncovered'}`}
                    >
                      {obRow.obligation.is_mandatory ? 'mandatory' : 'recommended'}
                    </span>
                  </span>
                )}
              </div>
              {obRow.obligation && obRow.obligation.accepted_sources.length > 0 && (
                <div
                  data-testid="accepted-sources"
                  style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 10 }}
                >
                  Accepted as proof:{' '}
                  <span style={{ color: 'var(--ink)' }}>
                    {acceptedSourcesLabel(obRow.obligation.accepted_sources)}
                  </span>
                  .
                </div>
              )}
              {obRow.failures.length > 0 && (
                <div data-testid="gate-failures" style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
                    Documents in hand that fell short:
                  </div>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {obRow.failures.map((f, i) => (
                      <li
                        key={`${f.failure}-${f.document_id ?? i}`}
                        style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}
                      >
                        {failureLabel(f.failure)}
                        {f.detail ? (
                          <span style={{ color: 'var(--faint)' }}>
                            {' '}
                            — {cleanEngineText(f.detail)}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          {req ? (
            <>
              <div
                style={{
                  fontSize: 'var(--text-12)',
                  marginTop: gap ? 12 : 0,
                  color: 'var(--muted)',
                }}
              >
                <Sentence
                  meaning={req.sent_at ? 'chase.row' : 'chase.row.unsent'}
                  values={{
                    obligation: obRow ? obligationLabel(obRow.obligation_code) : null,
                    object: targetTitle,
                    counterparty: req.counterparty_name || null,
                    sent: req.sent_at ?? null,
                  }}
                />
                {req.recipient_email ? (
                  <span className="mono" style={{ color: 'var(--faint)' }}>
                    {' '}
                    · {req.recipient_email}
                  </span>
                ) : null}
                {' · '}
                <span
                  className={`chip s-${req.status === 'fulfilled' ? 'verified' : req.status === 'overdue' || req.status === 'escalated' ? 'expired' : 'uncovered'}`}
                >
                  {req.status}
                </span>
              </div>
              <SlaTimeline req={req} />
              <DraftPreview requestId={req.id} />
            </>
          ) : isGapTask ? (
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 12 }}>
              {isObligationTask
                ? 'No request exists yet for this obligation.'
                : 'No request exists yet for this gap.'}
            </div>
          ) : (
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
              Request not found — it may have been cancelled or fulfilled already.
            </div>
          )}
        </div>
      </div>

      {/* actions */}
      <div style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {task.fund_id && <ChaseContactCard fundId={task.fund_id} onFund={onFund} />}
        {isGapTask && !req && (
          <div className="vcard" style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>Request the document</div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
              Drafts a request to the fund's configured counterparty with the policy deadline; you
              review the email before it goes.
            </div>
            <button
              className="vbtn pri"
              style={{ marginTop: 12 }}
              onClick={createRequest}
              /* Same value createRequest() guards on — one source of truth. */
              disabled={busy || !requestPath}
              data-testid="chase-create-request"
            >
              {/* "not yet", not "not allowed": while the row is still being
                  read the label carries the surface's usual loading voice
                  (lowercase + ellipsis, as in DocumentsPage / ObjectDrawer /
                  FundCockpit). Once the read has finished, a still-disabled
                  button means there is genuinely nothing to chase, and the
                  ordinary label is the honest thing to show. */}
              {loading ? 'loading…' : 'Create request'}
            </button>
          </div>
        )}

        {req && req.status === 'draft' && (
          <div className="vcard" style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>Send it</div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
              Marks the request sent and starts the SLA clock
              {req.deadline ? ` (deadline ${formatOfficerDate(req.deadline)})` : ''}.
            </div>
            <button
              className="vbtn pri"
              style={{ marginTop: 12 }}
              onClick={() => transition('sent')}
              disabled={busy}
            >
              Send request
            </button>
          </div>
        )}

        {/* Escalate — only with somebody to escalate TO (audit cut 11, Ale
            2026-09-05). No fund carries a senior contact today, so this card
            is dark until the counterparty register lands; the `escalated`
            status and the day-44 follow-up stay in the engine. */}
        {req && senior && (req.status === 'overdue' || req.status === 'sent') && (
          <div className="vcard" style={{ padding: 16 }} data-testid="chase-escalate-card">
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>Escalate</div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
              {req.status === 'overdue'
                ? `Past deadline — escalate to ${senior.name}.`
                : 'Not yet overdue; escalation unlocks once the deadline passes.'}
            </div>
            <button
              className="vbtn pri"
              style={{ marginTop: 12 }}
              onClick={() => transition('escalated')}
              disabled={busy || req.status !== 'overdue'}
            >
              Escalate
            </button>
          </div>
        )}

        {/* Waive: officer-level on obligation rows (Ale 2026-07-08 —
            waivers officer-only at pilot); legacy gaps stay manager-gated.
            The door is always shown; a rank below the rule sees it
            disabled with the rule text (Tier-1 C1 role honesty). */}
        {isGapTask && (gap || obRow) && (
          <div className="vcard" style={{ padding: 16 }} data-testid="waive-door">
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
              {obRow ? 'Waive the obligation' : 'Waive the gap'} <HelpDot k="action.waive" />
            </div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
              The policy demand is judged not applicable here — recorded permanently with your name
              and reason.
            </div>
            {!waiveOpen ? (
              <>
                <button
                  className="vbtn"
                  style={{ marginTop: 12 }}
                  onClick={() => setWaiveOpen(true)}
                  disabled={!waiveRule.allowed}
                  title={waiveRule.rule ?? undefined}
                  data-testid="waive-open"
                >
                  Waive…
                </button>
                {!waiveRule.allowed ? (
                  <div className="cn-rule" style={{ marginTop: 6 }} data-testid="waive-verb-rule">
                    {waiveRule.rule}
                  </div>
                ) : null}
              </>
            ) : (
              <div style={{ marginTop: 12 }}>
                <input
                  autoFocus
                  aria-label="Why this requirement is not applicable"
                  placeholder="why is this requirement not applicable? (required)"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  style={{
                    width: '100%',
                    font: 'inherit',
                    fontSize: 'var(--text-12)',
                    padding: '7px 10px',
                    border: '1.5px solid var(--line)',
                    borderRadius: 7,
                  }}
                />
                <button
                  className="vbtn pri"
                  style={{ marginTop: 10 }}
                  onClick={waive}
                  disabled={busy || !justification.trim()}
                >
                  {obRow ? 'Waive obligation' : 'Waive gap'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
